import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaStar } from "react-icons/fa";

const BASE_URL = "https://api.dreamtoday.in";

/* Gender → emoji avatar */
const getAvatar = (gender) => {
  if (gender === "female") return "👩";
  if (gender === "male") return "👨";
  return "🧑";
};

/* ── CARD ── */
const TestimonialCard = ({ testimonial }) => (
  <div className="min-w-[280px] md:min-w-[320px] lg:min-w-[340px]
    bg-gradient-to-br from-[#FFF9E6] via-[#FFF3CC] to-[#FFE8A3]
    rounded-2xl p-6 border border-[#F5D77A]
    shadow-[0_15px_35px_rgba(212,175,55,0.25)]
    hover:shadow-[0_20px_50px_rgba(212,175,55,0.45)]
    transition group relative">

    {/* USER */}
    <div className="flex items-center gap-4 mb-4">
      <div className="w-16 h-16 rounded-full
        bg-gradient-to-r from-[#FFE9A8] to-[#FFD86B]
        flex items-center justify-center text-3xl shadow-md">
        {getAvatar(testimonial.gender)}
      </div>
      <div>
        <h3 className="font-bold text-gray-900">{testimonial.name}</h3>
        <p className="text-sm text-gray-600">{testimonial.location}</p>
        <p className="text-xs text-yellow-700 mt-1 font-semibold">{testimonial.title}</p>
      </div>
    </div>

    {/* RATING — default 5 stars */}
    <div className="flex gap-1 mb-3">
      {[...Array(5)].map((_, i) => (
        <FaStar key={i} className={i < (testimonial.rating ?? 5) ? "text-yellow-600" : "text-yellow-200"} />
      ))}
    </div>

    {/* COMMENT */}
    <p className="text-gray-700 italic text-sm leading-relaxed">
      "{testimonial.feedback}"
    </p>

    {/* BIG QUOTE */}
    <div className="absolute bottom-2 right-4 text-7xl opacity-10 text-yellow-600">"</div>
  </div>
);

/* ── SKELETON CARD ── */
const SkeletonCard = () => (
  <div className="min-w-[280px] md:min-w-[320px] lg:min-w-[340px]
    bg-gradient-to-br from-[#FFF9E6] via-[#FFF3CC] to-[#FFE8A3]
    rounded-2xl p-6 border border-[#F5D77A] animate-pulse">
    <div className="flex items-center gap-4 mb-4">
      <div className="w-16 h-16 rounded-full bg-yellow-200" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-yellow-200 rounded w-2/3" />
        <div className="h-3 bg-yellow-100 rounded w-1/2" />
        <div className="h-3 bg-yellow-100 rounded w-3/4" />
      </div>
    </div>
    <div className="flex gap-1 mb-3">
      {[...Array(5)].map((_, i) => <div key={i} className="w-4 h-4 rounded bg-yellow-200" />)}
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-yellow-100 rounded" />
      <div className="h-3 bg-yellow-100 rounded w-5/6" />
      <div className="h-3 bg-yellow-100 rounded w-4/6" />
    </div>
  </div>
);

/* ── SECTION ── */
const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/alltestimonial`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setTestimonials(d.testimonials); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Hide section entirely if no data after load
  if (!loading && testimonials.length === 0) return null;

  // Duplicate for seamless marquee loop
  const loopData = testimonials.length > 0
    ? [...testimonials, ...testimonials]
    : Array(6).fill(null);

  return (
    <section className="py-24 bg-gradient-to-b from-white via-[#FFF7DB] to-white overflow-hidden">

      {/* HEADER */}
      <div className="text-center mb-14 px-4">
        <h2 className="text-4xl md:text-5xl font-bold mb-3">
          <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
            Reviews & Testimonials
          </span>
        </h2>
        <p className="text-gray-600">Real stories from real winners</p>
      </div>

      {/* MARQUEE */}
      {loading ? (
        // Static skeleton row while loading
        <div className="flex gap-6 px-4 overflow-hidden">
          {Array(4).fill(null).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : (
        <motion.div
          className="flex gap-6"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
        >
          {loopData.map((testimonial, index) => (
            <TestimonialCard key={`${testimonial._id}-${index}`} testimonial={testimonial} />
          ))}
        </motion.div>
      )}
    </section>
  );
};

export default Testimonials;